import React, { useRef, useCallback, useState, useEffect } from 'react';
import Webcam from 'react-webcam';
import { useNavigate } from 'react-router-dom';
import { Camera, RotateCw, Monitor, ImageIcon } from 'lucide-react';

const isMobileDevice = typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);

export default function CaptureStudentFace() {
  const webcamRef = useRef(null);
  const navigate = useNavigate();
  const [captured, setCaptured] = useState(null);
  const [deviceId, setDeviceId] = useState(null);
  const [videoConstraints, setVideoConstraints] = useState({ width: 640, height: 480, facingMode: isMobileDevice ? { ideal: 'environment' } : 'user' });
  const [devices, setDevices] = useState([]);
  const [errorMsg, setErrorMsg] = useState('');
  const [showDevices, setShowDevices] = useState(false);

  // Try to select a back camera by enumerating devices (mobile supportive)
  useEffect(() => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) return;

    let mounted = true;

    async function findBackCamera() {
      try {
        const list = await navigator.mediaDevices.enumerateDevices();
        if (!mounted) return;
        const videoInputs = list.filter(d => d.kind === 'videoinput');
        setDevices(videoInputs);

        // Prefer a device with 'back' or 'rear' in the label if labels are available (requires prior permission)
        let back = videoInputs.find(d => /back|rear|environment|camera_2/i.test(d.label));

        // If no label-based match, try to use the last device (commonly the back camera on many phones)
        if (!back && videoInputs.length > 1) {
          back = videoInputs[videoInputs.length - 1];
        }

        const saved = localStorage.getItem('preferredCameraId');
        if (saved && videoInputs.find(d => d.deviceId === saved)) {
          // honor persisted deviceId preference
          setDeviceId(saved);
          setVideoConstraints(prev => ({ ...prev, deviceId: { exact: saved } }));
        } else if (back) {
          setDeviceId(back.deviceId);
          setVideoConstraints(prev => ({ ...prev, deviceId: { exact: back.deviceId } }));
        } else {
          // fallback to facingMode ideal environment
          setVideoConstraints(prev => ({ ...prev, facingMode: isMobileDevice ? { ideal: 'environment' } : 'user' }));
        }
      } catch (err) {setErrorMsg('Could not list media devices. Make sure camera permissions are allowed.');
      }
    }

    findBackCamera();

    return () => { mounted = false; };
  }, []);

  // If the page is not secure (not https and not localhost), getUserMedia may be blocked on mobile.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const isSecure = window.isSecureContext || window.location.protocol === 'https:' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (!isSecure) {
        setErrorMsg('Access to device cameras is restricted on many mobile browsers when served over HTTP. Use HTTPS or an HTTPS tunnel (for example, ngrok) and allow camera permission in the browser.');
      }
    }
  }, []);

  const capture = useCallback(() => {
    if (!webcamRef.current) return;
    try {
      const imgSrc = webcamRef.current.getScreenshot();
      if (imgSrc) {sessionStorage.setItem('capturedFace', imgSrc);
        sessionStorage.setItem('capturedStudentFace', imgSrc);
        setCaptured(imgSrc);
      } else {setErrorMsg('Capture failed. Make sure the camera is enabled and visible.');
      }
    } catch (err) {setErrorMsg('Capture error: ' + (err.message || err));
    }
  }, [webcamRef]);

  const retake = () => {setCaptured(null);
    sessionStorage.removeItem('capturedStudentFace');
    sessionStorage.removeItem('capturedFace');
    setErrorMsg('');
  };

  const done = () => {navigate('/registerstudent');
  };

  const switchCamera = async () => {
    // rotate between available video inputs
    if (!devices || devices.length < 2) return;
    const currentIndex = devices.findIndex(d => d.deviceId === deviceId);
    const next = devices[(currentIndex + 1) % devices.length];
    if (next) {
      setDeviceId(next.deviceId);
      setVideoConstraints(prev => ({ ...prev, deviceId: { exact: next.deviceId }, facingMode: undefined }));
      // persist user's choice
      try { localStorage.setItem('preferredCameraId', next.deviceId); } catch (e) { /* ignore */ }
      setErrorMsg('');
    }
  };

  const chooseDevice = (d) => {
    setDeviceId(d.deviceId);
    setVideoConstraints(prev => ({ ...prev, deviceId: { exact: d.deviceId }, facingMode: undefined }));
    try { localStorage.setItem('preferredCameraId', d.deviceId); } catch (e) { /* ignore */ }
    setErrorMsg('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
  <div className="max-w-5xl mx-auto">
        <div className="bg-white rounded-xl shadow p-6 mb-6 border border-gray-100 flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center">
            <Camera className="text-blue-600" size={22} />
          </div>
          <div>
            <h2 className="text-2xl font-semibold">Capture Student Face</h2>
            <p className="text-sm text-gray-500">Take a clear photo of the student for face recognition. Use back camera for better results.</p>
          </div>
        </div>

        {errorMsg && (
          <div className="mb-4 p-3 bg-yellow-50 text-yellow-800 rounded border">{errorMsg}</div>
        )}

        <div className="bg-white rounded-xl shadow overflow-hidden border border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
            {/* Preview */}
            <div className="flex flex-col items-center">
              <div className="w-full bg-gray-100 rounded-lg overflow-hidden shadow-inner">
                {!captured ? (
                  <div className="relative bg-black aspect-video md:h-96 h-64">
                    <Webcam
                      audio={false}
                      ref={webcamRef}
                      screenshotFormat="image/jpeg"
                      videoConstraints={videoConstraints}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute left-3 top-3 bg-white/60 rounded px-2 py-1 text-xs text-gray-700">Live</div>
                  </div>
                ) : (
                  <img src={captured} alt="captured" className="w-full h-auto object-cover rounded" />
                )}
              </div>
              <div className="mt-4 flex flex-wrap gap-3">
                {!captured ? (
                  <>
                    <button onClick={capture} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg shadow">
                      <Camera size={16} /> Capture
                    </button>
                    <button onClick={() => navigate(-1)} className="px-4 py-2 bg-gray-100 rounded-lg">Cancel</button>
                    {devices && devices.length > 1 && (
                      <>
                        <button onClick={switchCamera} className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500 text-white rounded-lg">
                          <RotateCw size={16} /> Switch
                        </button>
                        <button onClick={() => setShowDevices(s => !s)} className="px-4 py-2 bg-gray-50 rounded-lg">{showDevices ? 'Hide' : 'Show'} Devices</button>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <button onClick={retake} className="px-4 py-2 bg-yellow-500 text-white rounded-lg">Retake</button>
                    <button onClick={done} className="px-4 py-2 bg-green-600 text-white rounded-lg">Done</button>
                  </>
                )}
              </div>
            </div>

            {/* Controls / Devices list */}
            <div>
              <div className="mb-4">
                <h3 className="text-lg font-medium">Camera & Devices</h3>
                <p className="text-sm text-gray-500">Choose preferred camera or switch between available inputs.</p>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded border">
                  <div className="text-sm text-gray-600 mb-2">Selected device</div>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium">{(devices.find(d => d.deviceId === deviceId)?.label) || (deviceId ? deviceId.slice(0,8) : 'Default Camera')}</div>
                    <div className="text-xs text-gray-400">{isMobileDevice ? 'Mobile' : 'Desktop'}</div>
                  </div>
                </div>

                {showDevices && devices && devices.length > 0 && (
                  <div className="bg-white p-3 rounded border">
                    <div className="text-sm text-gray-700 mb-2">Available video devices</div>
                    <ul className="space-y-2 max-h-48 overflow-auto">
                      {devices.map(d => (
                        <li key={d.deviceId} className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-sm">{d.label || 'Camera ' + d.deviceId.slice(0,6)}</div>
                            <div className="text-xs text-gray-400">id: {d.deviceId}</div>
                          </div>
                          <div>
                            <button onClick={() => chooseDevice(d)} className="px-3 py-1 bg-green-600 text-white rounded">Use</button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="bg-gray-50 p-3 rounded border">
                  <div className="text-sm font-medium mb-2">Tips</div>
                  <ul className="text-sm text-gray-600 list-disc list-inside space-y-1">
                    <li>Use the back camera on mobile for better quality.</li>
                    <li>Serve the page via HTTPS on mobile to avoid camera restrictions.</li>
                    <li>Tap <span className="font-medium">Show Devices</span> to select a specific camera.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
